
import { useMemo, useCallback, useContext } from 'react';
import { SET_ANNOTATION, SELECT_ANNOTATION, SELECT_TAB, SELECT_TOOL } from '../actions';
import useAnnotationDragHandlers from './useAnnotationDragHandlers';
import { EditorContext } from '../../../index';
import { TABS_IDS, TOOLS_IDS, WATERMARK_ANNOTATION_ID } from '../utils/constants';
import { isCanvasPanMode } from '../utils/canvasInteractionMode';
import getAnnotateToolIdFromKonvaNode from '../utils/getAnnotateToolIdFromKonvaNode';
import resolveAnnotateHitFromKonvaNode from '../utils/resolveAnnotateHitFromKonvaNode';
import getTextTransformPropsFromKonva from '../utils/getTextTransformPropsFromKonva';
import dispatchTextWithCardSync from '../utils/dispatchTextWithCardSync';
import { hasTemplateCardBackground } from '../utils/textTemplateCardLayout';
import useStore from './useStore';

const useAnnotationEvents = () => {
  const {
    tabId,
    toolId,
    dispatch,
    annotations = {},
    designLayer,
    editorPreferences = {},
  } = useStore();
  const panMode = isCanvasPanMode(editorPreferences);
  const { setCurrentTab } = useContext(EditorContext);
  const dragHandlers = useAnnotationDragHandlers();

  const updateAnnotation = useCallback(
    (annotationProps) => {
      dispatch({
        type: SET_ANNOTATION,
        payload: annotationProps,
      });
    },
    [dispatch],
  );

  const getAnnotationTransformProps = useCallback(
    (e) => {
      const annotationId = e.target.id();
      const transformProps = {
        id: annotationId,
        rotation: e.target.rotation(),
        x: e.target.x(),
        y: e.target.y(),
      };

      if (e.target.name() === TOOLS_IDS.TEXT) {
        return {
          ...transformProps,
          ...getTextTransformPropsFromKonva(e.target, annotations[annotationId]),
        };
      }

      transformProps.scaleX = e.target.scaleX();
      transformProps.scaleY = e.target.scaleY();
      return transformProps;
    },
    [annotations],
  );

  const updateAnnotationTransform = useCallback(
    (e) => {
      const annotationId = e.target.id();
      if (annotations[annotationId]?.locked) {
        return;
      }

      const transformProps = getAnnotationTransformProps(e);
      const mergedText = {
        ...annotations[annotationId],
        ...transformProps,
      };

      if (
        e.target.name() === TOOLS_IDS.TEXT &&
        hasTemplateCardBackground(annotations, annotationId)
      ) {
        dispatchTextWithCardSync(
          dispatch,
          annotations,
          mergedText,
          designLayer,
        );
        return;
      }

      updateAnnotation(transformProps);
    },
    [
      annotations,
      designLayer,
      dispatch,
      getAnnotationTransformProps,
      updateAnnotation,
    ],
  );

  const updateTextAnnotationOnTransform = useCallback(
    (e) => {
      if (annotations[e.target.id()]?.locked) {
        return;
      }
      if (e.target.name() === TOOLS_IDS.TEXT) {
        e.target.setAttrs(getAnnotationTransformProps(e));
      }
    },
    [annotations, getAnnotationTransformProps],
  );

  const selectAnnotationOnClick = useCallback(
    (e) => {
      if (panMode) {
        return;
      }
      if (e.target.id() === WATERMARK_ANNOTATION_ID) {
        return;
      }

      const resolved =
        resolveAnnotateHitFromKonvaNode(e.target, annotations) ??
        getAnnotateToolIdFromKonvaNode(e.target);
      const multiple = e.evt.ctrlKey || e.evt.shiftKey || e.evt.metaKey;
      const annotationId = resolved?.annotationId ?? e.target.id();

      if (resolved?.toolId) {
        const { toolId: annotationToolId } = resolved;
        const needsDrawTab = tabId !== TABS_IDS.ANNOTATE;
        const needsToolSwitch = toolId !== annotationToolId;

        if (needsDrawTab) {
          setCurrentTab(TABS_IDS.ANNOTATE);
          dispatch({
            type: SELECT_TAB,
            payload: {
              tabId: TABS_IDS.ANNOTATE,
              toolId: annotationToolId,
            },
          });
        } else if (needsToolSwitch) {
          dispatch({
            type: SELECT_TOOL,
            payload: {
              toolId: annotationToolId,
              keepSelections: true,
            },
          });
        }
      }

      dispatch({
        type: SELECT_ANNOTATION,
        payload: {
          annotationId,
          multiple,
        },
      });
    },
    [annotations, dispatch, panMode, setCurrentTab, tabId, toolId],
  );

  return useMemo(
    () => ({
      onTransform: updateTextAnnotationOnTransform,
      onTransformEnd: updateAnnotationTransform,
      onDragEnd: dragHandlers.onDragEnd,
      onDragMove: dragHandlers.onDragMove,
      onClick: selectAnnotationOnClick,
      onTap: selectAnnotationOnClick,
      onDragStart: dragHandlers.onDragStart,
    }),
    [
      updateTextAnnotationOnTransform,
      updateAnnotationTransform,
      dragHandlers.onDragMove,
      dragHandlers.onDragStart,
      dragHandlers.onDragEnd,
      selectAnnotationOnClick,
    ],
  );
};

export default useAnnotationEvents;
